import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Headphones, Search, Filter, Clock, User, AlertCircle, CheckCircle,
  MessageSquare, Send, Paperclip, X, ChevronDown, UserPlus, Eye,
  Download, Image, FileText, RefreshCw
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeSupport() {
  const { user, theme } = useOutletContext();
  const isDark = theme === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("active");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMyTickets, setShowMyTickets] = useState(false);
  
  // Reply
  const [replyMessage, setReplyMessage] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Dialogs
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showRequestInfoDialog, setShowRequestInfoDialog] = useState(false);
  const [requestInfoMessage, setRequestInfoMessage] = useState("");
  const [requestAttachments, setRequestAttachments] = useState(false);

  // Load tickets and staff on mount
  useEffect(() => {
    fetchTickets();
    fetchStaff();
  }, [statusFilter, priorityFilter, assigneeFilter]);
// Check for ticket param in URL
  useEffect(() => {
    const ticketId = searchParams.get('ticket');
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.ticket_id === ticketId);
      if (ticket) {
        handleSelectTicket(ticket);
      }
    }
  }, [searchParams, tickets]);
const fetchTickets = async () => {
    setLoading(true);
    try {
      let url = `${API}/support/admin/tickets?`;
      
      if (statusFilter !== "all") {
        url += `status=${statusFilter}&`;
      }
      if (statusFilter === "all") {
        url += `show_closed=true&`;
      }
      if (priorityFilter !== "all") {
        url += `priority=${priorityFilter}&`;
      }
      if (assigneeFilter !== "all") {
        url += `assigned_to=${assigneeFilter}&`;
      }

      const res = await axios.get(url, { withCredentials: true });
      setTickets(res.data.tickets || []);
      setStats(res.data.stats || {});
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/support/admin/staff`, { withCredentials: true });
      setStaff(res.data.staff || []);
    } catch (error) {
      console.error("Failed to load staff:", error);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticketId}`, { withCredentials: true });
      setTicketDetail(res.data.ticket);
    } catch (error) {
      toast.error("Failed to load ticket details");
    }
  };

  const handleSelectTicket = async (ticket) => {
    setSelectedTicket(ticket);
    await fetchTicketDetail(ticket.ticket_id);
    // Mark as read
    try {
      await axios.post(`${API}/support/tickets/${ticket.ticket_id}/mark-read`, {}, { withCredentials: true });
    } catch {
      // Silently ignore mark-read errors
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.ticket_id}/reply`, {
        message: replyMessage,
        is_internal: isInternal
      }, { withCredentials: true });
      
      setReplyMessage("");
      setIsInternal(false);
      await fetchTicketDetail(selectedTicket.ticket_id);
      await fetchTickets();
      toast.success(isInternal ? "Internal note added" : "Reply sent");
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleAssignTicket = async (staffId) => {
    try {
      await axios.post(`${API}/support/admin/tickets/${selectedTicket.ticket_id}/assign`, {
        assigned_to: staffId
      }, { withCredentials: true });
      
      setShowAssignDialog(false);
      await fetchTicketDetail(selectedTicket.ticket_id);
      await fetchTickets();
      toast.success("Ticket assigned");
    } catch (error) {
      toast.error("Failed to assign ticket");
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await axios.put(`${API}/support/admin/tickets/${selectedTicket.ticket_id}/status`, {
        status
      }, { withCredentials: true });
      
      await fetchTicketDetail(selectedTicket.ticket_id);
      await fetchTickets();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleRequestInfo = async () => {
    if (!requestInfoMessage.trim()) return;
    try {
      await axios.post(`${API}/support/admin/tickets/${selectedTicket.ticket_id}/request-info`, {
        message: requestInfoMessage,
        request_attachments: requestAttachments
      }, { withCredentials: true });
      
      setShowRequestInfoDialog(false);
      setRequestInfoMessage("");
      setRequestAttachments(false);
      await fetchTicketDetail(selectedTicket.ticket_id);
      await fetchTickets();
      toast.success("Information requested from customer");
    } catch (error) {
      toast.error("Failed to request info");
    }
  };

  const getStatusColor = (status) => {
    if (isDark) {
      const colors = {
        open: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        in_progress: "bg-amber-500/20 text-amber-400 border-amber-500/30",
        waiting_customer: "bg-purple-500/20 text-purple-400 border-purple-500/30",
        requested_info: "bg-orange-500/20 text-orange-400 border-orange-500/30",
        resolved: "bg-green-500/20 text-green-400 border-green-500/30",
        closed: "bg-slate-500/20 text-slate-400 border-slate-500/30"
      };
      return colors[status] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
    } else {
      const colors = {
        open: "bg-blue-100 text-blue-700 border-blue-200",
        in_progress: "bg-amber-100 text-amber-700 border-amber-200",
        waiting_customer: "bg-purple-100 text-purple-700 border-purple-200",
        requested_info: "bg-orange-100 text-orange-700 border-orange-200",
        resolved: "bg-green-100 text-green-700 border-green-200",
        closed: "bg-gray-100 text-gray-600 border-gray-200"
      };
      return colors[status] || "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: "bg-red-500 text-white",
      high: "bg-orange-500 text-white",
      medium: "bg-amber-500 text-white",
      low: "bg-green-500 text-white"
    };
    return colors[priority] || "bg-slate-500 text-white";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Filter tickets by search and my tickets
  const filteredTickets = tickets.filter(ticket => {
    // My tickets filter
    if (showMyTickets && ticket.assigned_to !== user?.user_id) {
      return false;
    }
    
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ticket.subject?.toLowerCase().includes(search) ||
      ticket.ticket_number?.toLowerCase().includes(search) ||
      ticket.user_email?.toLowerCase().includes(search) ||
      ticket.company_name?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Headphones className="w-6 h-6 text-violet-500" />
            Support Tickets
          </h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage and respond to customer support requests</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowMyTickets(!showMyTickets)} 
            variant={showMyTickets ? "default" : "outline"}
            className={showMyTickets ? "bg-violet-600 hover:bg-violet-700" : ""}
          >
            <User className="w-4 h-4 mr-2" />
            My Tickets
          </Button>
          <Button onClick={fetchTickets} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: "Active", value: stats.active, color: "text-blue-500" },
          { label: "Unassigned", value: stats.unassigned, color: "text-orange-500" },
          { label: "Urgent", value: stats.urgent, color: "text-red-500" },
          { label: "Waiting", value: stats.waiting_customer, color: "text-purple-500" },
          { label: "Resolved", value: stats.resolved, color: "text-green-500" },
          { label: "Total", value: stats.total, color: isDark ? "text-slate-400" : "text-gray-600" }
        ].map((stat) => (
          <Card key={stat.label} className={`p-3 text-center ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <p className={`text-xl font-bold ${stat.color}`}>{stat.value || 0}</p>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{stat.label}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tickets..."
              className={`pl-9 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className={`w-40 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="all">All Tickets</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="waiting_customer">Waiting Customer</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className={`w-32 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className={`w-48 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}>
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {staff.map((s) => (
                <SelectItem key={s.user_id} value={s.user_id}>
                  <div className="flex flex-col">
                    <span>{s.name || s.email?.split('@')[0]}</span>
                    <span className="text-xs text-gray-500">{s.email}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket List */}
        <Card className={`lg:col-span-1 overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {showMyTickets ? 'My Tickets' : `${filteredTickets.length} Tickets`}
            </h3>
          </div>
          <div className="overflow-y-auto max-h-[600px]">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500 mx-auto"></div>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className={`p-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                <Headphones className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{showMyTickets ? "No tickets assigned to you" : "No tickets found"}</p>
              </div>
            ) : (
              <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-100'}`}>
                {filteredTickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    onClick={() => handleSelectTicket(ticket)}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedTicket?.ticket_id === ticket.ticket_id 
                        ? isDark ? 'bg-slate-700' : 'bg-violet-50' 
                        : isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'
                    } ${ticket.unread_by_admin ? 'border-l-2 border-l-violet-500' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority?.charAt(0).toUpperCase()}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</p>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{ticket.ticket_number}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] border ${getStatusColor(ticket.status)}`}>
                            {ticket.status?.replace('_', ' ')}
                          </span>
                          {ticket.assigned_to_name ? (
                            <span className={`text-[10px] flex items-center ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                              <User className="w-3 h-3 mr-0.5" />
                              {ticket.assigned_to_name.split(' ')[0]}
                            </span>
                          ) : (
                            <span className="text-[10px] text-orange-500">Unassigned</span>
                          )}
                        </div>
                      </div>
                      <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        {formatDate(ticket.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Ticket Detail */}
        <Card className={`lg:col-span-2 overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          {!selectedTicket ? (
            <div className={`h-full flex items-center justify-center p-8 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Select a ticket to view details</p>
              </div>
            </div>
          ) : ticketDetail ? (
            <div className="flex flex-col h-full">
              {/* Ticket Header */}
              <div className={`p-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticketDetail.subject}</h3>
                    <div className={`flex items-center gap-2 mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      <span>{ticketDetail.ticket_number}</span>
                      <span>•</span>
                      <span>{ticketDetail.user_email}</span>
                      {ticketDetail.company_name && (
                        <>
                          <span>•</span>
                          <span>{ticketDetail.company_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedTicket(null);
                      setTicketDetail(null);
                    }}
                    className={`p-1 ${isDark ? 'text-slate-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Select value={ticketDetail.status} onValueChange={handleUpdateStatus}>
                    <SelectTrigger className={`w-40 ${getStatusColor(ticketDetail.status)} border`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="waiting_customer">Waiting Customer</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAssignDialog(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    {ticketDetail.assigned_to_name || "Assign"}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRequestInfoDialog(true)}
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Request Info
                  </Button>
                </div>
                
                {/* Customer Details Panel */}
                <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-blue-50'} border ${isDark ? 'border-slate-600' : 'border-blue-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-blue-700'}`}>
                      Customer Info
                    </span>
                    {ticketDetail.company_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs gap-1"
                        onClick={() => window.open(`/backoffice/customers/${ticketDetail.company_id}`, '_blank')}
                      >
                        <Eye className="w-3 h-3" /> View Full Profile
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Customer ID</span>
                      <p className={`font-mono font-medium ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                        {ticketDetail.customer_id || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Name</span>
                      <p className={isDark ? 'text-white' : 'text-gray-900'}>
                        {ticketDetail.user_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Email</span>
                      <p className={isDark ? 'text-white' : 'text-gray-900'}>
                        {ticketDetail.user_email || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Company</span>
                      <p className={isDark ? 'text-white' : 'text-gray-900'}>
                        {ticketDetail.company_name || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[350px]">
                {ticketDetail.messages?.map((msg, idx) => (
                  <div
                    key={msg.message_id || idx}
                    className={`flex ${msg.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      msg.is_internal 
                        ? 'bg-amber-100 border-2 border-amber-300' 
                        : msg.sender_type === 'staff'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 border border-gray-300'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${
                          msg.is_internal 
                            ? 'text-amber-800' 
                            : msg.sender_type === 'staff' 
                              ? 'text-blue-100' 
                              : 'text-gray-700'
                        }`}>
                          {msg.sender_type === 'staff' ? (msg.sender_name || 'Support') : (msg.sender_name || 'Customer')}
                        </span>
                        {msg.is_internal && (
                          <span className="text-[10px] bg-amber-300 text-amber-900 px-1.5 py-0.5 rounded font-semibold">
                            🔒 Internal Note
                          </span>
                        )}
                        {msg.is_info_request && (
                          <span className="text-[10px] bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded">
                            Info Request
                          </span>
                        )}
                      </div>
                      <p className={`text-sm whitespace-pre-wrap ${
                        msg.is_internal 
                          ? 'text-amber-900' 
                          : msg.sender_type === 'staff' 
                            ? 'text-white' 
                            : 'text-gray-800'
                      }`}>{msg.message}</p>
                      
                      {/* Attachments */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {msg.attachments.map((attachment, aIdx) => (
                            <a
                              key={aIdx}
                              href={attachment.startsWith('http') ? attachment : `${BACKEND_URL}${attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${
                                msg.sender_type === 'staff' && !msg.is_internal 
                                  ? 'bg-violet-500 text-white hover:bg-violet-400' 
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {attachment.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <Image className="w-3 h-3" />
                              ) : (
                                <FileText className="w-3 h-3" />
                              )}
                              <span className="truncate">{attachment.split('/').pop()}</span>
                              <Download className="w-3 h-3 ml-auto" />
                            </a>
                          ))}
                        </div>
                      )}
                      
                      <p className={`text-[10px] mt-1 ${msg.sender_type === 'staff' && !msg.is_internal ? 'text-violet-200' : msg.is_internal ? 'text-amber-600' : isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {formatDate(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              <div className={`p-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded"
                    />
                    Internal note (not visible to customer)
                  </label>
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder={isInternal ? "Add internal note..." : "Type your reply..."}
                    className={`flex-1 resize-none ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                    rows={2}
                  />
                  <Button
                    onClick={handleSendReply}
                    disabled={sending || !replyMessage.trim()}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
          )}
        </Card>
      </div>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Assign Ticket</DialogTitle>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Select an internal staff member to assign this ticket
            </p>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-[400px] overflow-y-auto">
            {staff.length === 0 ? (
              <p className={`text-center py-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                No staff members available
              </p>
            ) : (
              staff.map((s) => (
                <button
                  key={s.user_id}
                  onClick={() => handleAssignTicket(s.user_id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    ticketDetail?.assigned_to === s.user_id
                      ? 'bg-violet-600 text-white'
                      : isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm ${
                    ticketDetail?.assigned_to === s.user_id ? 'bg-violet-500' : isDark ? 'bg-slate-600' : 'bg-gray-300'
                  }`}>
                    {s.profile_image ? (
                      <img src={s.profile_image} alt={s.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      (s.name || s.email || '?')[0].toUpperCase()
                    )}
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-sm font-medium">{s.name || s.email?.split('@')[0]}</p>
                    <p className={`text-xs ${ticketDetail?.assigned_to === s.user_id ? 'text-violet-200' : isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {s.email}
                    </p>
                    <p className={`text-xs ${ticketDetail?.assigned_to === s.user_id ? 'text-violet-200' : isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      {s.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} {s.department ? `• ${s.department}` : ''}
                    </p>
                  </div>
                  {ticketDetail?.assigned_to === s.user_id && (
                    <CheckCircle className="w-5 h-5 text-green-400 ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={showRequestInfoDialog} onOpenChange={setShowRequestInfoDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Request Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              value={requestInfoMessage}
              onChange={(e) => setRequestInfoMessage(e.target.value)}
              placeholder="What information do you need from the customer?"
              className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
              rows={4}
            />
            <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              <input
                type="checkbox"
                checked={requestAttachments}
                onChange={(e) => setRequestAttachments(e.target.checked)}
                className="rounded"
              />
              <Paperclip className="w-4 h-4" />
              Allow customer to upload attachments
            </label>
            <Button
              onClick={handleRequestInfo}
              disabled={!requestInfoMessage.trim()}
              className="w-full bg-violet-600 hover:bg-violet-700"
            >
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
